import { prepareImageForUpload } from './utils/image';

// Use environment variable for API URL in production, fallback to relative path for dev proxy
const API_BASE = process.env.REACT_APP_API_URL 
  ? `${process.env.REACT_APP_API_URL}/api` 
  : '/api';

// Helper for making authenticated requests
async function request(url, options = {}) {
  const config = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(`${API_BASE}${url}`, config);
  
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: 'Request failed' }));
    const error = new Error(body.error || 'Request failed');
    error.status = response.status;
    error.data = body;
    throw error;
  }

  return response.json();
}

// AI responses carry the business's remaining credits (null = unlimited); the account
// context listens for this to keep the balance in the sidebar current.
export const CREDITS_EVENT = 'credits-changed';

const announceCredits = (credits) => {
  if (credits !== undefined) window.dispatchEvent(new CustomEvent(CREDITS_EVENT, { detail: credits }));
};

async function aiRequest(url, options) {
  try {
    const result = await request(url, options);
    announceCredits(result.credits);
    return result;
  } catch (error) {
    if (error.status === 402) announceCredits(0);
    throw error;
  }
}

// Tell the backend about a crash in the app (it counts them and emails an alert). Never throws.
export function reportClientError(error, componentStack = '') {
  try {
    fetch(`${API_BASE}/client-errors`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: String(error?.message || error || 'Unknown error').slice(0, 500),
        stack: `${error?.stack || ''}\n${componentStack || ''}`.slice(0, 3000),
        page: window.location.pathname,
      }),
    }).catch(() => {});
  } catch {
    // Reporting must never cause another error
  }
}

// Auth API
export const authApi = {
  login: (email, password) => request('/auth/login', { 
    method: 'POST', 
    body: { email, password } 
  }),
  
  // kind: 'business' | 'individual'; name is the business name or the person's name
  register: ({ kind, name, email, password }) => request('/auth/register', {
    method: 'POST',
    body: { kind, name, email, password },
  }),

  logout: () => request('/auth/logout', { method: 'POST' }),

  me: () => request('/auth/me'),
  
  changePassword: (currentPassword, newPassword) => request('/auth/change-password', {
    method: 'POST',
    body: { currentPassword, newPassword }
  }),

  // Emails a one-time reset link -> { message }
  forgotPassword: (email) => request('/auth/forgot-password', { method: 'POST', body: { email } }),

  // From the emailed link; signs the user in -> { user }
  resetPassword: (token, password) => request('/auth/reset-password', { method: 'POST', body: { token, password } }),

  // Which sign-in options are switched on -> { google_client_id }
  providers: () => request('/auth/providers'),

  // "Continue with Google" with the ID token from Google's button -> { user }. Without an account
  // and without kind/name it fails with status 404 and data { code: 'no_account', email, name }.
  google: (credential, { kind, name } = {}) => request('/auth/google', { method: 'POST', body: { credential, kind, name } }),
};

// Admin/Settings API
export const adminApi = {
  getSettings: () => request('/admin/settings'),
  
  updateSettings: (settings) => request('/admin/settings', {
    method: 'PUT',
    body: settings
  }),
  
  // Large phone photos are shrunk in the browser first (see utils/image.js)
  uploadImage: async (file, { maxEdge } = {}) => {
    const formData = new FormData();
    formData.append('image', await prepareImageForUpload(file, { maxEdge }));
    
    const response = await fetch(`${API_BASE}/admin/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    
    return response.json();
  },

  migrateImagesToR2: () => request('/admin/storage/migrate-images', { method: 'POST' }),

  // User management
  getUsers: () => request('/admin/users'),
  
  createUser: (email, password, role) => request('/admin/users', {
    method: 'POST',
    body: { email, password, role }
  }),

  updateUserRole: (id, role) => request(`/admin/users/${id}/role`, {
    method: 'PUT',
    body: { role }
  }),
  
  deleteUser: (id) => request(`/admin/users/${id}`, { method: 'DELETE' }),
  
  resetUserPassword: (id, password) => request(`/admin/users/${id}/reset-password`, {
    method: 'POST',
    body: { password }
  }),

  // SKU management
  getSkus: () => request('/admin/skus'),

  // System stats
  getStats: () => request('/admin/stats'),

  // Activity log
  getActivity: (limit = 50, offset = 0) => request(`/admin/activity?limit=${limit}&offset=${offset}`),

  // Webhook delivery health per platform
  getWebhookStatus: () => request('/admin/webhooks/status'),

  // Data export
  exportOrders: () => request('/admin/export/orders'),
  exportSkus: () => request('/admin/export/skus'),
};

// Storage API
export const storageApi = {
  getUnits: () => request('/storage/units'),
  
  getUnit: (id) => request(`/storage/units/${id}`),
  
  createUnit: (data) => request('/storage/units', {
    method: 'POST',
    body: data
  }),
  
  updateUnit: (id, data) => request(`/storage/units/${id}`, {
    method: 'PUT',
    body: data
  }),
  
  deleteUnit: (id) => request(`/storage/units/${id}`, { method: 'DELETE' }),
  
  createRegion: (unitId, data) => request(`/storage/units/${unitId}/regions`, {
    method: 'POST',
    body: data
  }),
  
  updateRegion: (unitId, regionId, data) => request(`/storage/units/${unitId}/regions/${regionId}`, {
    method: 'PUT',
    body: data
  }),
  
  deleteRegion: (unitId, regionId) => request(`/storage/units/${unitId}/regions/${regionId}`, {
    method: 'DELETE'
  }),
  
  batchUpdateRegions: (unitId, regions) => request(`/storage/units/${unitId}/regions/batch`, {
    method: 'PUT',
    body: { regions }
  }),
};

// Product names and photos, keyed by SKU: { [sku]: { name, image_url, has_own_photo } }
export const productsApi = {
  lookup: (skus) => request(`/products?skus=${encodeURIComponent(skus.join(','))}`),

  setPhoto: (sku, imageUrl) => request(`/products/${encodeURIComponent(sku)}/photo`, {
    method: 'PUT',
    body: { image_url: imageUrl }
  }),

  removePhoto: (sku) => request(`/products/${encodeURIComponent(sku)}/photo`, { method: 'DELETE' }),

  // Name shown in the app; an empty name goes back to the store's -> { sku, name, image_url, has_own_photo }
  rename: (sku, name) => request(`/products/${encodeURIComponent(sku)}/name`, { method: 'PUT', body: { name } }),

  // Every known product with the bins it's in: [{ sku, name, image_url, locations: [{ unit_id, unit_name, region_id, label }] }]
  catalog: () => request('/products/catalog'),

  // Stores products can be imported from: [{ platform, name, ready, missing }]
  importSources: () => request('/products/import/sources'),

  // One page of a store's products; call again with the returned cursor until it's null
  // -> { imported, cursor }
  importPage: (platform, cursor) => request('/products/import', { method: 'POST', body: { platform, cursor } }),
};

// AI features (only available when the Worker has an Anthropic API key). Each call spends a
// credit; with none left they fail with status 402.
export const aiApi = {
  // Suggested bins for a shelf photo; nothing is saved
  detectRegions: (unitId) => aiRequest(`/storage/units/${unitId}/detect-regions`, { method: 'POST' }),

  // messages: [{ role: 'user' | 'assistant', content }] -> { answer, locations }
  ask: (messages) => aiRequest('/assistant', { method: 'POST', body: { messages } }),

  // Which bin each product goes in, from the shelf photo. bins: the editor's current bins.
  // -> { suggestions: [{ sku, region_id, confidence, reason }] }
  suggestPlacement: (unitId, skus, bins) => aiRequest(`/storage/units/${unitId}/suggest-placement`, {
    method: 'POST',
    body: { skus, bins },
  }),
};

// One-click store connections. Each returns { url } to send the browser to the store to approve.
export const connectApi = {
  // { stores: [{ platform, name, can_connect, needs_address, countries?, connected, account, orders }] }
  list: () => request('/connect'),
  // address: the store's address, for stores that need one (Shopify, WooCommerce);
  // country: the country an Amazon seller sells in
  start: (platform, { address, country } = {}) => request(`/connect/${platform}`, {
    method: 'POST',
    body: { store_url: address, shop: address, country },
  }),
  // Finish an install that started in the store (BigCommerce, Wix): { platform, name }
  claim: (token) => request('/connect/claim', { method: 'POST', body: { token } }),
  disconnect: (platform) => request(`/connect/${platform}`, { method: 'DELETE' }),
};

// Bins whose counted stock is low: [{ sku, quantity, name, image_url, label, unit_id, unit_name }]
export const stockApi = {
  low: () => request('/stock/low'),
};

// Getting-started progress: { store_connected, shelves, first_shelf_id, bins, products, placed, completed_orders }
export const setupApi = {
  status: () => request('/setup'),
};

// AI credits and buying them
export const billingApi = {
  // { credits, unlimited_credits, cost, packs, currency, payments_enabled, history }
  get: () => request('/billing'),

  // -> { url } of a Stripe Checkout page
  checkout: (pack) => request('/billing/checkout', { method: 'POST', body: { pack } }),

  // Monthly plan: start it (-> { url } of a Stripe Checkout page), or cancel/resume it
  startPlan: () => request('/billing/plan', { method: 'POST' }),
  setPlanCancelled: (cancel) => request('/billing/plan', { method: 'PUT', body: { cancel } }),
};

// Staff invite links
export const invitesApi = {
  // Public: who the link is from -> { business_name, role }
  info: (token) => request(`/invites/${encodeURIComponent(token)}`),

  accept: (token, email, password) => request(`/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: { email, password },
  }),

  // Join with the ID token from Google's button instead of an email and password
  acceptWithGoogle: (token, credential) => request(`/invites/${encodeURIComponent(token)}/accept`, {
    method: 'POST',
    body: { credential },
  }),

  list: () => request('/admin/invites'),

  // -> { id, role, expires_at, url, sent_to, emailed }; the url is only shown once. With an email
  // the link is also sent there (emailed: false when email isn't set up or it failed).
  create: (role, email) => request('/admin/invites', { method: 'POST', body: { role, email: email || undefined } }),

  revoke: (id) => request(`/admin/invites/${id}`, { method: 'DELETE' }),
};

// Orders API
export const ordersApi = {
  getAll: (status) => request(`/orders${status ? `?status=${status}` : ''}`),
  
  getActive: () => request('/orders/active'),
  
  getById: (id) => request(`/orders/${id}`),
  
  updateStatus: (id, status) => request(`/orders/${id}/status`, {
    method: 'PUT',
    body: { status }
  }),
  
  delete: (id) => request(`/orders/${id}`, { method: 'DELETE' }),

  create: (orderNumber, items) => request('/orders', {
    method: 'POST',
    body: { order_number: orderNumber, items }
  }),

  bulkUpdateStatus: (orderIds, status) => request('/orders/bulk-status', {
    method: 'POST',
    body: { orderIds, status }
  }),

  bulkDelete: (orderIds) => request('/orders/bulk-delete', {
    method: 'DELETE',
    body: { orderIds }
  }),
};
